import { apiGet, apiPut, apiPost, validateRequiredParams, annualReportUrls } from '@/services/BaseApiService';


/**
 * Retrieves a diagnostic imaging report by its ID.
 * 
 * @param {number} reportId - The unique identifier of the report
 * @returns {Promise<Object>} The report data
 * @throws {Error} If reportId is invalid or if the API request fails
 */
export const getDIReport = async (reportId) => {
  validateRequiredParams({ reportId });
  return await apiGet(`${annualReportUrls.DI}/${reportId}`);
};

/**
 * Creates a new diagnostic imaging report.
 * 
 * @param {number} organizationId - The ID of the organization submitting the report
 * @param {number} periodId - The reporting period ID
 * @param {number} contactId - The contact person ID
 * @param {number} roleId - The role ID of the contact person
 * @returns {Promise<Object>} The created report data
 * @throws {Error} If any parameter is invalid or if the API request fails
 */
export const createDIReport = async (organizationId, periodId, contactId, roleId) => {
  validateRequiredParams({ organizationId, periodId, contactId });
  return await apiPost(`${annualReportUrls.DI}`, { organizationId, periodId, contactId, roleId });
};

/**
 * Updates the status of a diagnostic imaging report.
 * 
 * @param {number} reportId - The unique identifier of the report
 * @param {number} statusId - The new status ID
 * @param {boolean} isCertified - Whether the report is certified
 * @returns {Promise<void>} A promise that resolves when the update is complete
 * @throws {Error} If any parameter is invalid or if the API request fails
 */
export const updateReportStatus = async (reportId, statusId, isCertified) => {
  validateRequiredParams({ reportId, statusId, isCertified });
  return await apiPut(`${annualReportUrls.DI}/${reportId}/status`, { reportId, reportStatusId: statusId, isCertified });
};

/**
 * Updates the CFO reporting months for a diagnostic imaging report.
 * 
 * @param {number} reportId - The unique identifier of the report
 * @param {string} cfoName - The name of the CFO
 * @param {number} reportingMonths - The number of months being reported
 * @returns {Promise<void>} A promise that resolves when the update is complete
 * @throws {Error} If any parameter is invalid or if the API request fails
 */
export const updateCfoReportingMonths = async (reportId, cfoName, reportingMonths) => {
  validateRequiredParams({ reportId, cfoName, reportingMonths });
  return await apiPut(`${annualReportUrls.DI}/${reportId}`, { reportId, cfoName, reportingMonths });
};

export const addReportContact = async (reportId, contactId, roleId) => {
  validateRequiredParams({ reportId, contactId });
  return await apiPost(`${annualReportUrls.DI}/${reportId}/contacts`, { reportId, contactId, roleId });
};


